import { createAsyncThunk } from '@reduxjs/toolkit';
import { getApiBaseUrl } from './WithcookieSlice';

// 유니레벨 트리 데이터를 가져오는 액션 생성
export const fetchUnilevelTree = createAsyncThunk(
  'withcookie/fetchUnilevelTree',
  async ({ id, depth = 8, one_upper = false }: { id?: string | number, depth?: string | number, one_upper?: boolean } = {}, { rejectWithValue, getState }) => {
    try {
      // 사용자 ID 확인
      let userId = id;
      if (!userId) {
        const state = getState() as any;
        const user = state.withcookie.user || state.withcookie.userInfo;
        
        if (user && user.id) {
          userId = String(user.id);
        } else {
          return rejectWithValue('사용자 ID가 없습니다. 로그인이 필요합니다.');
        }
      }
      
      const apiBaseUrl = getApiBaseUrl();
      const endpoint = `${apiBaseUrl}/api/users/get_tree`; // get_binary_tree 대신 get_tree 사용
      // depth를 문자열로 변환하여 요청
      const requestBody: Record<string, any> = { 
        id: String(userId), 
        depth: String(depth)
      };
      if (userId){
        const state = getState() as any;
        const user = state.withcookie.user || state.withcookie.userInfo;
        requestBody.requestUserId = String(user.id);
      }

      // one_upper 값이 true인 경우에만 추가
      if (one_upper === true) {
        requestBody.one_upper = true;
      }
      
      const response = await fetch(
        endpoint,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );
      
      // 응답이 실패인 경우 JSON 파싱 전에 처리
      if (!response.ok) {
        try {
          const text = await response.text();

          // JSON 파싱 시도
          try {
            const data = JSON.parse(text);
            if (data.message) {
              return rejectWithValue(data.message);
            }
          } catch (parseError) {
          }
        } catch (textError) {
        }
        return rejectWithValue(`API 오류: ${response.status}`);
      }
      
      const data = await response.json();
      
      // 데이터 변환 함수 - API 응답 필드를 컴포넌트가 기대하는 필드로 매핑
      const transformNode = (node: any): any => {
        if (!node) return null;
        
        return {
          ...node,
          // title1을 title로 매핑
          title: node.title1 ? node.title1.replace('성함: ', '') : node.title || node.name,
          // class를 isShipper로 매핑 (조건부)
          isShipper: node.class === 'shipper',
          // 자식 노드들도 재귀적으로 변환
          children: Array.isArray(node.children) 
            ? node.children.map(transformNode) 
            : []
        };
      };
      
      return transformNode(data);
    } catch (error: any) {
      console.error('[유니레벨 트리] API 오류:', error);
      // 네트워크 오류나 기타 예외 상황
      return rejectWithValue('네트워크 오류가 발생했습니다.');
    }
  }
);

// 유니레벨 트리 노드 추가 데이터 가져오기 (플러스 버튼용)
export const fetchUnilevelTreeNodeChildren = createAsyncThunk(
  'withcookie/fetchUnilevelTreeNodeChildren',
  async ({ id, depth = 8 }: { id: string | number, depth?: string | number }, { rejectWithValue, getState }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const endpoint = `${apiBaseUrl}/api/users/get_tree`; // get_binary_tree 대신 get_tree 사용
      
      const state = getState() as any;
      const user = state.withcookie.user || state.withcookie.userInfo;
      
      const requestBody: Record<string, any> = { 
        id: String(id), 
        depth: String(depth)
      };
      
      if (user && user.id) {
        requestBody.requestUserId = String(user.id);
      }
      
      const response = await fetch(
        endpoint,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        if (data.message) {
          return rejectWithValue(data.message);
        }
        return rejectWithValue(data.error || '노드 데이터를 가져오는데 실패했습니다.');
      }
      
      if (data.success === false) {
        return rejectWithValue(data.message || data.error || '노드 데이터를 가져오는데 실패했습니다.');
      }
      
      // nodeId와 children 데이터를 함께 반환
      return {
        nodeId: id,
        nodeData: data.data || data
      };
    } catch (error: any) {
      return rejectWithValue(error.message || '네트워크 오류가 발생했습니다.');
    }
  }
);